import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";

const CHARACTER_BASE = "https://content.code-pro.app/character";

/** Builds the full image URL for a drawable name. */
const image = (name: string): string => `${CHARACTER_BASE}/${name}.png`;

/** Zero-pads a number to 2 digits (1 -> "01"), like String.format("%02d"). */
const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Same as Android safeParse(): returns the default when the value is not a number. */
const safeParse = (value: string | undefined, fallback = 0): number => {
    const parsed = parseInt(value ?? "", 10);
    return Number.isNaN(parsed) ? fallback : parsed;
};

/**
 * Same as Android findSkinIndexedDrawable().
 * Candidates in order:
 *   {prefix}{skin+1}_{NN}  ->  {prefix}{skin+1}_{n}  ->  {prefix}{skin+1}
 */
const skinIndexedCandidates = (prefix: string, skin: number, variant: number): string[] => [
    `${prefix}${skin + 1}_${pad2(variant + 1)}`,
    `${prefix}${skin + 1}_${variant + 1}`,
    `${prefix}${skin + 1}`
];

// Candidate image names for every layer, in the order they are tried
interface CharacterLayers {
    background: string[];
    body: string[];
    face: string[];
    ears: string[];
    eyes: string[];
    eyebrows: string[];
    mouth: string[];
    nose: string[];
    hairBack: string[] | null; // null when the code has no hair part
    hairFront: string[] | null;
}

interface LayerImageProps {
    names: string[];
    className: string;
}

/**
 * Renders the first candidate that loads successfully.
 * Mirrors Android's findDrawableBySeveralNames() + setImageIfFound():
 * when a candidate fails to load, the next one is tried;
 * when all fail, nothing is rendered (the layer is skipped).
 */
function LayerImage({ names, className }: LayerImageProps) {
    const [attempt, setAttempt] = useState(0);

    if (attempt >= names.length) return null;

    return (
        <img
            src={image(names[attempt])}
            alt=""
            className={className}
            onError={() => setAttempt((current) => current + 1)}
        />
    );
}

interface CharacterImageProps {
    /**
     * skin_body_face_eyes_eyebrows_mouth_nose_ears_bg_hair
     * Example: 1_0_3_0_1_2_0_0_1_2
     */
    characterCode?: string | null;
    editable?: boolean;
    onEdit?: () => void;
    className?: string;
    editLabel?: string; // Accessible name of the edit button
}

export default function CharacterImage({
                                           characterCode,
                                           editable = false,
                                           onEdit,
                                           className,
                                           editLabel = "ویرایش شخصیت"
                                       }: CharacterImageProps) {
    // Resolve all candidate names once per characterCode
    const layers = useMemo<CharacterLayers | null>(() => {
        if (!characterCode) return null;

        const parts = characterCode.split("_");
        if (parts.length < 9) return null;

        const skin = safeParse(parts[0]);
        const bodyIndex = safeParse(parts[1]);
        const faceIndex = safeParse(parts[2]);
        const eyesIndex = safeParse(parts[3]);
        const eyebrowsIndex = safeParse(parts[4]);
        const mouthIndex = safeParse(parts[5]);
        const noseIndex = safeParse(parts[6]);
        const earsIndex = safeParse(parts[7]);
        const bgIndex = safeParse(parts[8]);
        // Hair is optional: -1 means "no hair part in the code"
        const hairIndex = parts.length > 9 ? safeParse(parts[9]) : -1;
        const hairNumber = hairIndex + 1;

        return {
            background: [`character_bg_${bgIndex}`],

            // Body falls back to "body_base" if no skin-indexed image exists
            body: [...skinIndexedCandidates("body_skin", skin, bodyIndex), "body_base"],
            face: skinIndexedCandidates("face_skin", skin, faceIndex),
            ears: skinIndexedCandidates("ears_skin", skin, earsIndex),

            eyes: [`eyes${pad2(eyesIndex + 1)}`, `eyes_${eyesIndex + 1}`],
            eyebrows: [`eyebrows_${eyebrowsIndex + 1}`, `eyebrows_${pad2(eyebrowsIndex + 1)}`],
            mouth: [`mouth_${mouthIndex + 1}`, "mouth"],
            nose: [`nose_${noseIndex + 1}`, "nose_1", "nose_2"],

            hairBack:
                hairIndex >= 0 ? [`hair${hairNumber}_back`, `hair_back_${hairNumber}`] : null,
            hairFront:
                hairIndex >= 0
                    ? [
                        `hair${hairNumber}_front`,
                        `hair_front_${hairNumber}`,
                        `hair${hairNumber}_single`,
                        `hair${hairNumber}`
                    ]
                    : null
        };
    }, [characterCode]);

    if (!layers) return null;

    return (
        // key resets every layer's fallback state when the character changes
        <div
            key={characterCode}
            className={classNames("relative w-48 h-48 rounded-md overflow-hidden", className)}
        >
            {/* BACKGROUND (skipped if the image does not exist, like Android) */}
            <LayerImage
                names={layers.background}
                className="absolute inset-0 w-full h-full object-cover"
            />

            {/* BACK HAIR */}
            {layers.hairBack && (
                <LayerImage
                    names={layers.hairBack}
                    className="absolute left-1/2 top-1/2 w-[350px] -translate-x-1/2 -translate-y-[20px]"
                />
            )}

            {/* BODY */}
            <LayerImage
                names={layers.body}
                className="absolute left-1/2 bottom-0 h-[340px] -translate-x-1/2 translate-y-[16px]"
            />

            {/* FACE */}
            <LayerImage
                names={layers.face}
                className="absolute left-1/2 bottom-0 w-[240px] -translate-y-[20px] -translate-x-1/2"
            />

            {/* EARS */}
            <LayerImage
                names={layers.ears}
                className="absolute left-1/2 bottom-0 h-[210px] -translate-x-1/2"
            />

            {/* NOSE */}
            <LayerImage
                names={layers.nose}
                className="absolute left-1/2 top-1/2 w-[10px] -translate-x-1/2 -translate-y-[20px] z-10"
            />

            {/* EYES */}
            <LayerImage
                names={layers.eyes}
                className="absolute left-1/2 top-1/2 w-[45px] -translate-x-1/2 -translate-y-[30px] z-10"
            />

            {/* EYEBROWS */}
            <LayerImage
                names={layers.eyebrows}
                className="absolute left-1/2 top-1/2 w-[60px] -translate-x-1/2 -translate-y-[40px] z-10"
            />

            {/* MOUTH */}
            <LayerImage
                names={layers.mouth}
                className="absolute left-1/2 top-1/2 h-[16px] -translate-x-1/2 translate-y-[5px] z-10"
            />

            {/* FRONT HAIR */}
            {layers.hairFront && (
                <LayerImage
                    names={layers.hairFront}
                    className="absolute left-1/2 bottom-0 w-[350px] -translate-x-1/2 -translate-y-[20px] z-10"
                />
            )}

            {/* EDIT BUTTON */}
            {editable && (
                <button
                    type="button"
                    onClick={onEdit}
                    aria-label={editLabel}
                    className="absolute left-3 top-3 z-20 w-12 h-12 rounded-full flex items-center justify-center bg-gray-700 border-b-4 border-gray-900 hover:bg-gray-900 active:border-b-0 cursor-pointer transition"
                >
                    <FontAwesomeIcon icon={faPencil} className="text-white" />
                </button>
            )}
        </div>
    );
}